import { categoryLabels } from '../utils/clientDocumentIntake';
import { documentNameMatchesTarget, eligibleDocumentTargets } from './kycDocumentTargets';
import idDocIcon from '../images/id-card-blue-doc.svg';
import addressDocIcon from '../images/utility-blue-doc.svg';
import licenceDocIcon from '../images/corporate-doc-blue.svg';
import cvDocIcon from '../images/paper.svg';
import wealthDocIcon from '../images/sow-blue.svg';
import scanOkIcon from '../images/scann-ok-1.svg';
import warningIcon from '../images/warning-a.svg';
import individualIcon from '../images/avatar.svg';
import entityIcon from '../images/businessTrade.svg';
import birthdayIcon from '../images/birthday.svg';
import passportIcon from '../images/passport.svg';
import countryIcon from '../images/earth-location.svg';
import checkedIcon from '../images/checked-circle.svg';

// "Who does this document belong to?": the dropped document, the name read on it,
// and one card per person or entity of the KYC that can hold it.

const DOCUMENT_ICONS = { id_document: idDocIcon, proof_of_address: addressDocIcon, trade_licence: licenceDocIcon, cv: cvDocIcon, sow_document: wealthDocIcon };
const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

function countryText(value) {
  const text = String(value || '').trim();
  if (/^[A-Za-z]{2}$/.test(text)) {
    try { return countryNames.of(text.toUpperCase()) || text; } catch { return text; }
  }
  return text;
}

function dateText(value) {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : value instanceof Date ? value
    : /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : String(value).slice(0, 10);
}

function targetFacts(target) {
  const c = target.customer || {};
  const entity = target.type === 'entity';
  return [
    { icon: birthdayIcon, label: entity ? 'Incorporated' : 'Born', text: dateText(c.dobOrIncorporationDate) },
    { icon: passportIcon, label: entity ? 'Country of incorporation' : 'Nationality', text: countryText(c.nationalityOrIncorporationCountry) },
    { icon: countryIcon, label: 'Country', text: countryText(c.country) },
  ].filter((fact) => fact.text);
}

export default function KycDocumentOwnerChooser({ document, targets, onChoose }) {
  const holder = document.extraction?.fields?.clientName?.value || '';
  const eligible = eligibleDocumentTargets(document, targets)
    .map((target, index) => ({ target, index, match: documentNameMatchesTarget(document, target) }))
    .sort((a, b) => Number(b.match) - Number(a.match) || a.index - b.index);

  return <div className="kycOwnerPick">
    <div className="kycOwnerDoc">
      <span className="kycOwnerDocIcon" aria-hidden="true"><img src={DOCUMENT_ICONS[document.category] || cvDocIcon} alt="" /></span>
      <div className="kycOwnerDocText">
        <strong>{categoryLabels[document.category] || 'Document'}</strong>
        <span title={document.file?.name}>{document.file?.name}</span>
      </div>
      <div className={`kycOwnerRead${holder ? '' : ' isUnknown'}`}>
        <img src={holder ? scanOkIcon : warningIcon} alt="" aria-hidden="true" />
        <div>
          <small>Name on document</small>
          <strong>{holder || 'Not clearly identified'}</strong>
        </div>
      </div>
    </div>

    {eligible.length ? <>
      <p className="kycOwnerPrompt">Select the person or entity this document belongs to.</p>
      <div className="kycOwnerGrid">
        {eligible.map(({ target, match }) => <button type="button" key={target.id} className={`kycOwnerCard${match ? ' isMatch' : ''}`}
          onClick={() => onChoose(target.id)} aria-label={`${target.name}${target.role ? `, ${target.role}` : ''}${match ? ', name matches the document' : ''}`}>
          {match && <span className="kycOwnerMatch"><img src={checkedIcon} alt="" aria-hidden="true" />Name matches</span>}
          <span className="kycOwnerHead">
            <span className="kycOwnerAvatar" aria-hidden="true"><img src={target.type === 'entity' ? entityIcon : individualIcon} alt="" /></span>
            <span className="kycOwnerName">{target.name}</span>
          </span>
          <span className="kycOwnerRoles">
            {target.role.split(' · ').filter(Boolean).map((role) => <span key={role} className="kycOwnerRole">{role}</span>)}
          </span>
          {targetFacts(target).length > 0 && <span className="kycOwnerFacts">
            {targetFacts(target).map((fact) => <span key={fact.label} title={fact.label}><img src={fact.icon} alt="" aria-hidden="true" />{fact.text}</span>)}
          </span>}
          <i className="bi bi-arrow-right kycOwnerGo" aria-hidden="true" />
        </button>)}
      </div>
    </> : <div className="kycOwnerEmpty" role="note">
      <img src={warningIcon} alt="" aria-hidden="true" />
      <p>No corresponding person or entity is available in this KYC. Add their details first, then upload the document again.</p>
    </div>}
  </div>;
}
